import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { useReadSearchInfiniteQuery } from 'apis/search';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ContactsCompany } from './components/company/contactsCompany';

const ContactListMeta = ({ children }: PropsWithChildren) => {
  return (
    <FlexBox justifyContent="center" alignItems="center" style={{ height: 72 }}>
      {children}
    </FlexBox>
  );
};

export const ContactsCompanies = (): JSX.Element => {
  const { id } = useAppPathParams('/contacts/company/:id');
  const { params } = useAppSearchParams('/contacts/company/:id');

  const {
    results: companies,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useReadSearchInfiniteQuery({
    scope: 'companies',
    query: params.q ?? '',
    isStrict: (params.q && params.strict === 'true') || undefined,
    sortBy: { field: 'name', order: 'asc' },
    pageSize: 50,
  });

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('companyModalCompanyAdded', () => {
      // Delaying the refetch to give ElasticSearch some time to index the new user.
      setTimeout(() => {
        refetch();
      }, 2000);
    });

    return () => unsubscribe();
  }, [refetch]);

  if (isError) {
    return (
      <ContactListMeta>
        <Text.Paragraph>
          An error occurred while loading the companies
        </Text.Paragraph>
      </ContactListMeta>
    );
  }

  if (isLoading && companies.length === 0) {
    return (
      <ContactListMeta>
        <Spinner />
      </ContactListMeta>
    );
  }

  if (isSuccess && companies.length === 0) {
    return (
      <ContactListMeta>
        <Text.Paragraph>No companies found</Text.Paragraph>
      </ContactListMeta>
    );
  }

  return (
    <>
      {companies.map((company) => (
        <ContactsCompany
          key={company.id}
          company={company}
          selected={company.id === id}
        />
      ))}

      {isFetching && (
        <ContactListMeta>
          <Spinner />
        </ContactListMeta>
      )}

      {hasNextPage && !isFetching && <Waypoint onEnter={fetchNextPage} />}
    </>
  );
};
